import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import GetApp from '@mui/icons-material/GetApp'

import { findProductById } from 'common/constants'
import { addMonthUTC, dateToYearMonthDayString, endOfMonthUTC, startOfMonthUTC } from 'common/api/v1/helpers'
import type { ListResult } from 'common/api/v1/types'
import { findLicenseTypeById } from 'common-billing-server/index'
import type { BillingPeriod, Customer } from 'common-billing-server/types'

import { shrinkableColumnSx } from 'common-ui/components/Table/Table'
import { useBillingApi } from '../../api/billing/billing-context'
import { Path } from '../../routes/path'
import { useStatefulNavigate } from '../common/hooks/hook-stateful-navigate'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { Column, DataFetchingPaginatedTable, RefreshableDataFetchingPaginatedTable, Row } from '../common/Table'
import { AutoComplete } from '../common/AutoComplete'
import { DateRangePicker } from '../common/DateRangePicker'
import Wrapper from '../common/Wrapper'

type BillingTableRow = BillingPeriod & Pick<Row, 'id'>

const columns: Column<BillingTableRow, ''>[] = [
    { title: 'Customer name', valueForColumn: (p) => p.customer.name },
    { title: 'Customer number', valueForColumn: (p) => p.customer.customerNumber },
    { title: 'Product', valueForColumn: (p) => findProductById(p.license.product)?.name },
    { title: 'License key', valueForColumn: (p) => p.license.licenseKey },
    { title: 'License type', valueForColumn: (p) => findLicenseTypeById(p.license.licenseType)?.name },
    { title: 'Host', valueForColumn: (p) => p.host },
    { title: 'From', valueForColumn: (p) => dateToYearMonthDayString(p.startDate) },
    { title: 'To', valueForColumn: (p) => dateToYearMonthDayString(p.endDate) },
    { title: 'Contracted stream credits', valueForColumn: (p) => p.license.creditType },
    { title: 'Ingress credits', valueForColumn: (p) => p.ingressCredits },
    { title: 'Egress credits', valueForColumn: (p) => p.egressCredits },
    { title: 'Total credits', valueForColumn: (p) => p.totalCredits },
]

export const BillingPage = () => {
    const billingApi = useBillingApi()
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const { navigate } = useStatefulNavigate()
    const navigateToLicenseBilling = ({ license, startDate, endDate }: BillingTableRow) =>
        navigate(
            `${Path.billingDetails}?licenseId=${
                license.id
            }&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
        )
    const [customer, setCustomer] = useState<Customer | null>(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const customerId = searchParams.get('customerId') || undefined
    const startDateUrlParam =
        searchParams.get('startDate') ?? startOfMonthUTC(addMonthUTC(new Date(), -1)).toISOString()
    const endDateUrlParam = searchParams.get('endDate') ?? endOfMonthUTC(new Date()).toISOString()
    const { minDate, maxDate } = {
        minDate: addMonthUTC(new Date(endDateUrlParam), -4),
        maxDate: addMonthUTC(new Date(startDateUrlParam), 4),
    }
    const { addFailedOperation, errorBanner } = useFailedOperationBanner()

    useEffect(() => {
        let cancelled = false
        setCustomer(null)
        if (customerId) {
            void billingApi.listCustomers({ filter: { customerId }, limit: 1 }).then(
                ({ items }) => {
                    if (!cancelled) setCustomer(items[0] ?? null)
                },
                (error) => {
                    if (!cancelled)
                        addFailedOperation({ id: 'get-billing-customer', message: 'Failed fetching customer', error })
                }
            )
        }
        return () => {
            cancelled = true
        }
    }, [billingApi, customerId, addFailedOperation])

    const setDate = (key: 'startDate' | 'endDate', value: Date | null) => {
        if (!value || !value.getTime()) return

        searchParams.set(key, value.toISOString())
        setSearchParams(searchParams, { replace: true })
        tableRef.current?.refreshData()
    }

    const fetchBillings = async ({
        skip,
        limit,
    }: {
        skip?: number
        limit?: number
    }): Promise<ListResult<BillingTableRow>> => {
        try {
            const res = await billingApi.listBillings(
                { skip, limit },
                {
                    customerId,
                    startDate: startDateUrlParam,
                    endDate: endDateUrlParam,
                }
            )
            return {
                ...res,
                items: res.items
                    .map((p) => ({
                        ...p,
                        id: `${p.license.id}-${p.host}-${p.startDate.toISOString()}`,
                    }))
                    .sort((a, b) => a.customer.name.localeCompare(b.customer.name)),
            }
        } catch (error) {
            addFailedOperation({
                id: 'get-billing',
                message: 'Failed fetching billing',
            })
            throw error
        }
    }

    const changeCustomer = (c: Customer | null) => {
        setCustomer(c)
        c ? searchParams.set('customerId', c.id) : searchParams.delete('customerId')
        setSearchParams(searchParams, { replace: true })
        tableRef.current?.refreshData()
    }

    function makeBillingReportsCsvUrl(startDateString: string, endDateString: string) {
        const searchParams = new URLSearchParams()
        if (customerId) searchParams.set('customerId', customerId)
        searchParams.set('startDate', startDateString)
        searchParams.set('endDate', endDateString)
        searchParams.set('format', 'csv')
        searchParams.set('q', JSON.stringify({ skip: 0, limit: 10000 })) // TODO: Don't hardcode
        return `/api/billing?${searchParams.toString()}`
    }

    return (
        <Wrapper fillHeight>
            <Paper style={{ padding: '10px' }}>
                <Grid container spacing={2} sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <AutoComplete<Customer>
                            placeholder={`Customer`}
                            value={customer}
                            isRequired
                            onValueSelected={(c) => changeCustomer(c)}
                            formatSelectedValue={(customer) => customer.name}
                            isClearable
                            api={(query) =>
                                billingApi.listCustomers({ ...query, filter: { searchName: query.filter } })
                            }
                            dataTestId={'billing-customer'}
                            margin="none"
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 'auto' }}>
                        <DateRangePicker
                            minDate={minDate}
                            startDateString={startDateUrlParam}
                            endDateString={endDateUrlParam}
                            maxDate={maxDate}
                            onChange={setDate}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 'auto' }}>
                        <Link underline="none" href={makeBillingReportsCsvUrl(startDateUrlParam, endDateUrlParam)}>
                            <Button variant="outlined" startIcon={<GetApp />}>
                                Download
                            </Button>
                        </Link>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ ...shrinkableColumnSx, mt: 2 }}>
                <DataFetchingPaginatedTable<BillingTableRow, '', Record<string, never>>
                    stickyHeader
                    key={`${customerId}:${startDateUrlParam}:${endDateUrlParam}`}
                    myRef={tableRef}
                    api={fetchBillings}
                    columns={columns}
                    onRowClicked={navigateToLicenseBilling}
                    emptyRowsMessage={'No billing found'}
                />
            </Box>

            {errorBanner}
        </Wrapper>
    )
}
